import React, { useEffect, useRef, useState, useCallback } from 'react';
import { motion, useInView, useAnimation, AnimatePresence } from 'framer-motion';
import { useInView as useInViewObserver } from 'react-intersection-observer';
import ApplicationFormModal from './ApplicationFormModal';
import { 
  ArrowRight, 
  Users, 
  Code, 
  TrendingUp, 
  Building, 
  Mail, 
  CheckCircle,
  Star,
  Zap,
  Brain,
  Target,
  Globe,
  X,
  User,
  Phone,
  Briefcase,
  MessageSquare,
  Linkedin
} from 'lucide-react';

const LandingPage = () => {
  const heroRef = useRef(null);
  const controls = useAnimation();
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [showStickyButton, setShowStickyButton] = useState(false);

  // Handle sticky button visibility with better performance optimization
  useEffect(() => {
    let ticking = false;
    let lastScrollY = 0;
    
    const handleScroll = () => {
      const scrollY = window.scrollY;
      
      // Only update if scroll position changed significantly
      if (Math.abs(scrollY - lastScrollY) > 10) {
        if (!ticking) {
          requestAnimationFrame(() => {
            const heroHeight = window.innerHeight;
            setShowStickyButton(scrollY > heroHeight - 100);
            lastScrollY = scrollY;
            ticking = false;
          });
          ticking = true;
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle form submission
  const handleFormSubmit = useCallback(async (formData) => {
    try {
      console.log('Submitting form data:', formData);
      console.log('Backend URL:', process.env.REACT_APP_BACKEND_URL);
      
      const apiUrl = `${process.env.REACT_APP_BACKEND_URL}/api/submit-application`;
      console.log('Full API URL:', apiUrl);
      
      // Call backend API instead of Airtable directly
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone || '',
          linkedin: formData.linkedin,
          role: formData.role,
          cofounderType: formData.cofounderType,
          location: formData.location || '',
          experience: formData.experience,
          idea: formData.idea
        })
      });

      console.log('Response status:', response.status);
      const result = await response.json();
      console.log('Response data:', result);
      
      if (response.ok && result.success) {
        alert('Application submitted successfully! We will be in touch soon.');
        setShowApplicationForm(false);
      } else {
        console.error('Backend error:', result);
        throw new Error(result.detail || 'Failed to submit application');
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      alert('There was an error submitting your application. Please try again or email us directly at hello@together.fund');
    }
  }, []);

  // Stable animation hook for sections - optimized to prevent flickering
  const useAnimationInView = useCallback(() => {
    const { ref, inView } = useInViewObserver({
      threshold: 0.1,
      triggerOnce: true,
      rootMargin: '50px 0px'
    });
    
    return { ref, inView };
  }, []);

  // Optimized animation variants to prevent flickering
  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1
      }
    }
  };

  const staggerItem = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.4, ease: "easeOut" }
    }
  };

  const scaleOnHover = {
    scale: 1.02,
    transition: { duration: 0.2, ease: "easeOut" }
  };

  // Portfolio companies data
  const portfolioCompanies = [
    { name: "Composio.dev", description: "Integration platform for agents", funding: "$25M (Lightspeed US)" },
    { name: "Emergent.sh", description: "AI agents for app creation", funding: "$5.5M seed" },
    { name: "Spry", description: "AI physiotherapy platform", stage: "Series B" },
    { name: "Metaforms.ai", description: "AI agents for market research", stage: "Closing Series A" },
    { name: "RapidClaims.ai", description: "AI agents for medical coding", stage: "Closing Series A" }
  ];

  // Sticky Apply Button Component
  const StickyApplyButton = () => (
    <AnimatePresence>
      {showStickyButton && (
        <motion.div
          initial={{ opacity: 0, y: 100, scale: 0.8 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 100, scale: 0.8 }}
          className="fixed bottom-6 right-6 z-40"
        >
          {/* Desktop version */}
          <motion.button
            onClick={() => setShowApplicationForm(true)}
            className="hidden md:flex group relative px-8 py-4 bg-[#fb5b51] hover:bg-[#fb5b51]/90 rounded-full font-bold text-white text-lg shadow-2xl shadow-[#fb5b51]/25 hover:shadow-[#fb5b51]/40 items-center"
            whileHover={{ 
              scale: 1.05,
              boxShadow: "0 25px 50px -12px rgba(251, 91, 81, 0.4)"
            }}
            whileTap={{ scale: 0.95 }}
          >
            <span className="relative z-10 flex items-center">
              Apply Now
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </span>
            <div className="absolute inset-0 bg-[#fb5b51]/80 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></div>
            
            {/* Pulse animation */}
            <div className="absolute inset-0 bg-[#fb5b51] rounded-full animate-ping opacity-20"></div>
          </motion.button>
          
          {/* Mobile version */}
          <motion.button
            onClick={() => setShowApplicationForm(true)}
            className="md:hidden group relative w-14 h-14 bg-[#fb5b51] hover:bg-[#fb5b51]/90 rounded-full font-bold text-white shadow-2xl shadow-[#fb5b51]/25 hover:shadow-[#fb5b51]/40 flex items-center justify-center"
            whileHover={{ 
              scale: 1.1,
              boxShadow: "0 25px 50px -12px rgba(251, 91, 81, 0.4)"
            }}
            whileTap={{ scale: 0.9 }}
          >
            <span className="relative z-10">
              <ArrowRight className="w-6 h-6" />
            </span>
            <div className="absolute inset-0 bg-[#fb5b51]/80 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></div>
            <div className="absolute inset-0 bg-[#fb5b51] rounded-full animate-ping opacity-20"></div>
          </motion.button>
        </motion.div>
      )}
    </AnimatePresence>
  );

  const HeroSection = () => {
    const { ref, inView } = useAnimationInView();
    
    return (
      <motion.section 
        ref={ref}
        className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#180a2b]"
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
        variants={staggerContainer}
      >
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1660165458059-57cfb6cc87e5" 
            alt="AI Technology Background"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-[#180a2b]/95 via-[#180a2b]/90 to-[#180a2b]/95"></div>
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-6 text-center">
          <motion.div variants={staggerItem}>
            {/* Together Fund Logo */}
            <motion.div 
              className="flex justify-center mb-8"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <img 
                src="https://together.fund/wp-content/themes/togather/assets/image/logo.svg" 
                alt="Together Fund Logo"
                className="h-12 md:h-16 w-auto filter brightness-0 invert"
              />
            </motion.div>

            <motion.div 
              className="inline-flex items-center px-6 py-3 rounded-full bg-[#fb5b51]/20 backdrop-blur-sm border border-[#fb5b51]/30 mb-8"
              whileHover={{ scale: 1.05, backgroundColor: "rgba(251, 91, 81, 0.3)" }}
            >
              <Zap className="w-5 h-5 text-[#fb5b51] mr-2" />
              <span className="text-[#fb5b51]/90 font-medium">AI Studio Co-Founder Matching</span>
            </motion.div>
          </motion.div>

          <motion.h1 
            variants={staggerItem}
            className="text-6xl md:text-8xl font-black text-white mb-12 leading-tight"
          >
            <span className="block bg-gradient-to-r from-[#fb5b51] via-[#fb5b51] to-[#fb5b51]/80 bg-clip-text text-transparent">
              The Co-Founder
            </span>
            Connection
          </motion.h1>

          <motion.div variants={staggerItem} className="flex justify-center">
            <motion.button
              onClick={() => setShowApplicationForm(true)}
              className="group relative px-12 py-4 bg-[#fb5b51] hover:bg-[#fb5b51]/90 rounded-xl font-bold text-white text-xl shadow-2xl shadow-[#fb5b51]/25"
              whileHover={scaleOnHover}
              whileTap={{ scale: 0.95 }}
            >
              <span className="relative z-10 flex items-center">
                Apply Now
                <ArrowRight className="ml-2 w-6 h-6 group-hover:translate-x-1 transition-transform" />
              </span>
              <div className="absolute inset-0 bg-[#fb5b51]/80 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
            </motion.button>
          </motion.div>

          <motion.div 
            variants={staggerItem}
            className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8 text-center"
          >
            <div className="flex flex-col items-center">
              <div className="text-3xl md:text-4xl font-black text-[#fb5b51] mb-2">850+</div>
              <div className="text-slate-300 text-sm md:text-base">Applications</div>
            </div>
            <div className="flex flex-col items-center">
              <div className="text-3xl md:text-4xl font-black text-[#fb5b51] mb-2">$200M+</div>
              <div className="text-slate-300 text-sm md:text-base">AUM</div>
            </div>
            <div className="flex flex-col items-center">
              <div className="text-3xl md:text-4xl font-black text-[#fb5b51] mb-2">35+</div>
              <div className="text-slate-300 text-sm md:text-base">Portfolio Companies</div>
            </div>
            <div className="flex flex-col items-center">
              <div className="text-3xl md:text-4xl font-black text-[#fb5b51] mb-2">$1M-$12M</div>
              <div className="text-slate-300 text-sm md:text-base">Investment Range</div>
            </div>
          </motion.div>
        </div>
      </motion.section>
    );
  };

  const ProblemSection = () => {
    return (
      <section className="py-24 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <h2 className="text-5xl font-black text-slate-900 mb-8">
            Better Together
          </h2>
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl p-8 shadow-xl border border-slate-200">
              <p className="text-xl text-slate-700 leading-relaxed">
                Earlier this year, we launched{' '}
                <a 
                  href="https://www.together.fund/ai" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="font-bold text-[#fb5b51] hover:text-[#fb5b51]/80 transition-colors underline decoration-2 underline-offset-2"
                >
                  Together AI Studio
                </a>{' '}
                and discovered something fascinating. 
                While we received applications from exceptional AI talent across the globe, one clear pattern emerged: 
                <span className="font-bold text-[#fb5b51]"> many brilliant founders were actively seeking co-founders</span> — 
                technical builders looking for strong GTM partners, and business leaders searching for technical co-founders. 
                That's why we created the AI Studio Co-Founder Matching Program.
              </p>
            </div>
          </div>
        </div>
      </section>
    );
  };

  const TargetPersonasSection = () => {
    const personas = [
      {
        title: "Product Leaders",
        icon: Target,
        image: "https://images.unsplash.com/photo-1606588984221-7ab3e2bb038a",
        description: "You've built successful products, are deeply tuned into user needs, and are increasingly drawn to the potential of AI. You may already be leading or prototyping AI initiatives."
      },
      {
        title: "AI Technical Builders",
        icon: Brain,
        image: "https://images.unsplash.com/photo-1610129800642-4c4e609a18e1",
        description: "You're working at the cutting edge — from models and LLM infra to agentic frameworks and OSS tooling"
      },
      {
        title: "Business & GTM Leaders",
        icon: TrendingUp,
        image: "https://images.unsplash.com/photo-1496180470114-6ef490f3ff22",
        description: "You've led revenue, operations, or GTM at high-growth startups focusing on India market or India/US corridor. You understand how to scale, drive adoption, and craft narratives."
      }
    ];

    return (
      <section className="py-24 bg-[#180a2b]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-black text-white mb-6">Who Is This For?</h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              We're matching deeply ambitious individuals who are ready to take on foundational roles 
              in the companies of tomorrow.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {personas.map((persona, index) => {
              const IconComponent = persona.icon;
              return (
                <div
                  key={index}
                  className="group relative bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20 transition-colors duration-300 overflow-hidden"
                >
                  <div className="relative h-56 mb-6 rounded-xl overflow-hidden">
                    <img 
                      src={persona.image} 
                      alt={persona.title}
                      className="w-full h-full object-cover transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 p-3 bg-[#fb5b51]/90 backdrop-blur-sm rounded-xl">
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  
                  <h3 className="text-2xl font-bold text-white mb-4">{persona.title}</h3>
                  <p className="text-slate-300 leading-relaxed">{persona.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    );
  };

  const PortfolioSection = () => {
    const { ref, inView } = useAnimationInView();
    
    // Featured portfolio companies (main section)
    const featuredCompanies = [
      {
        name: "Composio",
        description: "Integration Platform for AI Agents & LLMs",
        logo: "https://avatars.githubusercontent.com/u/148457800?s=200&v=4",
        website: "https://composio.dev",
        category: "AI Infrastructure"
      },
      {
        name: "Emergent",
        description: "AI coding assistant",
        logo: "https://app.emergent.sh/logo-text.svg",
        website: "https://emergent.sh",
        category: "Developer Tools"
      },
      {
        name: "RapidClaims",
        description: "AI Agents for Healthcare automation",
        logo: "https://cdn.prod.website-files.com/6846c90a13b9a82f202adec1/6846c90a13b9a82f202ae011_Frame%2072.png",
        website: "https://www.rapidclaims.ai",
        category: "Healthcare"
      },
      {
        name: "Metaforms",
        description: "AI agents for Market Research",
        logo: "https://framerusercontent.com/assets/UNq0bTeM9VzOJfkcBU9VGYx1lg.jpg",
        website: "https://metaforms.ai",
        category: "Business Intelligence"
      },
      {
        name: "Spry",
        description: "AI-first Practice Management for Physiotherapists",
        logo: "https://cdn.prod.website-files.com/63e0af60b5381d312eb85d43/667d0af25f876e36c01ce666_purple%20logo.svg",
        website: "https://www.sprypt.com",
        category: "Healthcare"
      },
      {
        name: "Hunar",
        description: "AI Recruiter for frontline workforce",
        logo: "https://framerusercontent.com/images/8wJmfOLHVl8kYqvlNgwV2DsbxE.png",
        website: "https://www.hunar.ai",
        category: "HR Tech"
      }
    ];

    // Additional portfolio companies (compact section)
    const additionalCompanies = [
      { name: "Chaotix", description: "AI-led Game Creation Platform", website: "https://chaotix.ai" },
      { name: "Privado", description: "AI-first Privacy Solutions", website: "https://www.privado.ai" },
      { name: "Protecto", description: "LLM Guardrails for Enterprise", website: "https://protecto.ai" },
      { name: "Unstract", description: "AI-first Unstructured Data Extraction", website: "https://unstract.com" },
      { name: "Confido", description: "AI Voice Agents for Healthcare", website: "https://confido.ai" },
      { name: "Dhiwise", description: "Agentic AI for IT Services", website: "https://www.dhiwise.com" },
      { name: "Jhana", description: "AI Paralegal for Law firms", website: "https://jhana.ai" },
      { name: "Nex", description: "AI-first CRM Platform", website: "https://nex.ai" },
      { name: "Presentations AI", description: "AI-powered Presentation Creation", website: "https://presentations.ai" },
      { name: "Thesys", description: "Design Co-pilot for AI agents", website: "https://thesys.ai" }
    ];

    const FeaturedLogoCard = ({ company }) => (
      <motion.a
        href={company.website}
        target="_blank"
        rel="noopener noreferrer"
        className="group block relative bg-white rounded-2xl border border-slate-200 shadow-lg transition-shadow duration-300 overflow-hidden p-8 cursor-pointer"
        whileHover={{ 
          y: -8, 
          scale: 1.02,
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)"
        }}
      >
        {/* Logo Section */}
        <div className="flex items-center justify-center mb-6 h-24 bg-slate-50 rounded-xl overflow-hidden">
          {company.logo ? (
            <img 
              src={company.logo} 
              alt={`${company.name} logo`}
              className={`max-w-full max-h-full object-contain p-2 group-hover:scale-110 transition-transform duration-300 ${
                company.name === 'Spry' ? 'max-w-[60%] max-h-[60%]' : ''
              }`}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <div className={`${company.logo ? 'hidden' : 'flex'} items-center justify-center w-full h-full bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg`}>
            <span className="text-white font-bold text-2xl">
              {company.name.charAt(0)}
            </span>
          </div>
        </div>

        {/* Company Info */}
        <div className="text-center">
          <div className="mb-3">
            <span className="inline-block px-3 py-1 bg-[#fb5b51]/10 text-[#fb5b51] text-xs font-medium rounded-full">
              {company.category}
            </span>
          </div>
          <h3 className="font-bold text-slate-900 text-xl mb-2">
            {company.name}
          </h3>
          <p className="text-slate-600 text-sm leading-relaxed">
            {company.description}
          </p>
        </div>

        {/* External link indicator */}
        <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="w-6 h-6 bg-[#fb5b51] rounded-full flex items-center justify-center">
            <ArrowRight className="w-3 h-3 text-white" />
          </div>
        </div>

        {/* Hover Effect Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/0 to-cyan-500/0 group-hover:from-blue-500/5 group-hover:to-cyan-500/5 transition-all duration-500 rounded-2xl pointer-events-none" />
      </motion.a>
    );
    
    return (
      <motion.section 
        ref={ref}
        className="py-24 bg-slate-50 relative overflow-hidden"
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
        variants={staggerContainer}
      >
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <motion.div variants={staggerItem} className="text-center mb-16">
            <h2 className="text-5xl font-black text-slate-900 mb-6">Our Portfolio</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto mb-8">
              Building the future of AI with exceptional founders across diverse industries
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm text-slate-500">
              <span className="px-4 py-2 bg-white rounded-full shadow-sm">$1M-$12M Investment Range</span>
              <span className="px-4 py-2 bg-white rounded-full shadow-sm">India-US Corridor</span>
              <span className="px-4 py-2 bg-white rounded-full shadow-sm">35+ Portfolio Companies</span>
            </div>
          </motion.div>

          {/* Featured Companies */}
          <motion.div 
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16"
          >
            {featuredCompanies.map((company, index) => (
              <motion.div key={index} variants={staggerItem}>
                <FeaturedLogoCard company={company} />
              </motion.div>
            ))}
          </motion.div>

          {/* Additional Companies - Ultra Compact */}
          <motion.div variants={staggerItem} className="mb-12">
            <h3 className="text-2xl font-bold text-slate-900 mb-8 text-center">Additional Portfolio Companies</h3>
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {additionalCompanies.map((company, index) => (
                  <motion.a
                    key={index}
                    href={company.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center justify-between p-4 rounded-xl border border-slate-100 hover:border-[#fb5b51]/30 hover:bg-[#fb5b51]/5 transition-all duration-300"
                    whileHover={{ x: 4 }}
                  >
                    <div>
                      <h4 className="font-bold text-slate-900 text-sm">{company.name}</h4>
                      <p className="text-slate-600 text-xs leading-relaxed">{company.description}</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-[#fb5b51] transition-colors flex-shrink-0 ml-2" />
                  </motion.a>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Stealth Companies */}
          <motion.div variants={staggerItem}>
            <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl p-8 text-white text-center">
              <h3 className="text-2xl font-bold mb-4">Stealth Companies</h3>
              <p className="text-slate-300 leading-relaxed max-w-3xl mx-auto">
                Teams from <span className="font-semibold text-white">Google DeepMind</span> (new foundation models), 
                <span className="font-semibold text-white"> IIT Delhi</span> (browser automation), and 
                <span className="font-semibold text-white"> IIT Roorkee</span> (workflow automation using agents)
              </p>
            </div>
          </motion.div>
        </div>
      </motion.section>
    );
  };



  const CTASection = () => {
    const { ref, inView } = useAnimationInView();
    
    return (
      <motion.section 
        ref={ref}
        className="py-24 bg-[#fb5b51] relative overflow-hidden"
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
        variants={staggerContainer}
      >
        {/* Animated Background Elements */}
        <div className="absolute inset-0">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-white/20 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [-20, 20, -20],
                opacity: [0.2, 0.8, 0.2],
              }}
              transition={{
                duration: 4 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 2,
              }}
            />
          ))}
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <motion.div variants={staggerItem}>
            <h2 className="text-5xl md:text-6xl font-black text-white mb-6">
              Ready to Build?
            </h2>
            <p className="text-xl text-orange-100 mb-8 leading-relaxed">
              If you are interested in being part of the founding team of some iconic AI companies, 
              let's connect and find your perfect co-founder match.
            </p>
          </motion.div>

          <motion.div 
            variants={staggerItem}
            className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20 mb-8"
          >
            <div className="flex items-center justify-center mb-6">
              <Mail className="w-8 h-8 text-white mr-3" />
              <span className="text-2xl font-bold text-white">hello@together.fund</span>
            </div>
            <p className="text-orange-100 mb-6">
              Tell us your background — and we'll help you meet your co-founder(s).
            </p>
            
            <motion.button
              onClick={() => setShowApplicationForm(true)}
              className="group relative px-12 py-4 bg-white text-[#fb5b51] rounded-xl font-bold text-lg shadow-2xl"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              <span className="relative z-10 flex items-center">
                <Mail className="mr-2 w-5 h-5" />
                Apply Now
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </span>
            </motion.button>
          </motion.div>

          <motion.div variants={staggerItem}>
            <h3 className="text-2xl font-bold text-white mb-4">Let's build something legendary.</h3>
            <p className="text-orange-100 text-sm">
              All information shared with us — including background, intent, or interest — 
              will be treated with full confidentiality.
            </p>
          </motion.div>
        </div>
      </motion.section>
    );
  };

  return (
    <div className="relative">
      <ApplicationFormModal 
        isOpen={showApplicationForm} 
        onClose={() => setShowApplicationForm(false)} 
        onSubmit={handleFormSubmit} 
      />
      <StickyApplyButton />
      <HeroSection />
      <ProblemSection />
      <TargetPersonasSection />
      <PortfolioSection />
      <CTASection />
    </div>
  );
};

export default LandingPage;